import { createSelector } from 'reselect';
import createSelectors from './common/utils/selectorUtil';

import * as fromApi from './common/api/apiSelectors';
import * as fromAudio from './common/audio/audioSelectors';
import * as fromAlex from './common/alex/AlexSelectors';
import * as fromAvatar from './common/avatar/avatarSelectors';
import * as fromForms from './common/api/formsSelectors';
import * as fromMaze from './maze/mazeSelectors';
import * as fromPlaylist from './common/playlist/playlistSelectors';
// issues with default state
import * as fromQuestions from './common/questions/questionsSelectors';
import * as fromRan from './ran/ranSelectors';
import * as fromResult from './common/api/resultSelectors';
import * as fromResume from './common/resume/resumeSelectors';
import * as fromSpelling from './spelling/spellingSelectors';
import * as fromScreen from './common/screen/screenSelectors';
// issues with default state
import * as fromStimulus from './common/stimulus/stimulusButtonSelectors';
import * as fromTrc from './trc/trcSelectors';
import * as fromUser from './common/user/userSelectors';
import fromVocab from './vocab/vocabSelectors';
import * as fromRecording from './common/recording/recordingSelectors';
import * as fromTimeTracking from './common/timeTracking/timeTrackingSelectors';
import * as fromWR from './wr/wrSelectors';
import * as fromLC from './lc/lcSelectors';
import * as fromUserInteraction from './common/userInteraction/userInteractionSelectors';

import {
    TRO_PASSING_LEVEL_STATUSES,
    TRO_BOOK_CATEGORIES,
    TRO_BOOK_RELATIVE_LEVEL,
} from './constants/book';
import { ASSESSMENT_TYPE, COMPREHENSION_MEASURE } from './constants/measure';

import * as fromSecureBrowser from './common/secureBrowser/secureBrowserSelectors';

const MAX_BOOK_RESULT_AMOUNT = 2;
const SECOND_BOOK_RESULT_INDEX = 1;
const NO_LEVEL = null;

const selectors = {
    ...createSelectors(fromApi, 'api'),
    ...createSelectors(fromAudio, 'audio'),
    ...createSelectors(fromAlex, 'alex'),
    ...createSelectors(fromAvatar, 'avatar'),
    ...createSelectors(fromForms, 'forms'),
    ...createSelectors(fromMaze, 'maze'),
    ...createSelectors(fromPlaylist, 'playlist'),
    ...createSelectors(fromQuestions, 'questions'),
    ...createSelectors(fromTimeTracking, 'timeTracking'),
    ...createSelectors(fromRan, 'ran'),
    ...createSelectors(fromResult, 'result'),
    ...createSelectors(fromResume, 'resume'),
    ...createSelectors(fromScreen, 'screen'),
    ...createSelectors(fromSpelling, 'spelling'),
    ...createSelectors(fromStimulus, 'stimulus'),
    ...createSelectors(fromTrc, 'trc'),
    ...createSelectors(fromTrc.selectors, 'trc'),
    ...createSelectors(fromUser, 'user'),
    ...createSelectors(fromVocab, 'vocab'),
    ...createSelectors(fromRecording, 'recording'),
    ...createSelectors(fromWR, 'wr'),
    ...createSelectors(fromLC, 'lc'),
    ...createSelectors(fromLC.selectors, 'lc'),
    ...createSelectors(fromSecureBrowser, 'secureBrowser'),
    ...createSelectors(fromUserInteraction, 'userInteraction'),
};

selectors.getNextResultItem = createSelector(
    [selectors.findResultFor, selectors.getCurrentResults],
    (measureResults, resumedResults) => {
        if (measureResults) {
            const results = Array.isArray(measureResults) ? measureResults : [measureResults];
            if (results.length > 0 && Object.keys(resumedResults).length > 0) {
                const result = results.find(
                    r =>
                        Object.keys(resumedResults).includes(r.id) &&
                        resumedResults[r.id].formId === r.formId,
                );
                return result && resumedResults[result.id].nextItem;
            }
        }
        return undefined;
    },
);

selectors.getResumeResultFormId = createSelector(
    [selectors.findResultFor, selectors.getCurrentResults],
    (result, resumedResults) => {
        if (result && resumedResults[result.id]) {
            return resumedResults[result.id].formId === result.formId && result.formId;
        }
        return undefined;
    },
);

selectors.getNextTROBookLevel = createSelector([selectors.getTRCBookResults], bookResults => {
    const { STARTING_BOOK, LOW_BOOK, HIGH_BOOK } = TRO_BOOK_CATEGORIES;

    if (bookResults.length <= 0) return TRO_BOOK_RELATIVE_LEVEL[STARTING_BOOK];

    const areBothResultsCompleted =
        bookResults.length === MAX_BOOK_RESULT_AMOUNT &&
        (bookResults[SECOND_BOOK_RESULT_INDEX].passingLevel === TRO_PASSING_LEVEL_STATUSES.PASS ||
            bookResults[SECOND_BOOK_RESULT_INDEX].passingLevel ===
                TRO_PASSING_LEVEL_STATUSES.NO_PASS);

    if (areBothResultsCompleted) return NO_LEVEL;

    const currentBookIndex = bookResults.length - 1;
    const currentResult = bookResults[currentBookIndex];
    const { passingLevel } = currentResult;

    if (passingLevel === TRO_PASSING_LEVEL_STATUSES.PASS) return TRO_BOOK_RELATIVE_LEVEL[HIGH_BOOK];

    if (passingLevel === TRO_PASSING_LEVEL_STATUSES.NO_PASS)
        return TRO_BOOK_RELATIVE_LEVEL[LOW_BOOK];

    return NO_LEVEL;
});

selectors.getCurrentBookId = createSelector([selectors.getTRCBookResults], bookResults => {
    const currentBookIndex = bookResults.length - 1;
    const currentResult = bookResults[currentBookIndex];
    return currentResult.bookId;
});

selectors.getIsCurrentBookDone = createSelector([selectors.getTRCBookResults], bookResults => {
    if (bookResults.length <= 0) return true;
    const currentBookIndex = bookResults.length - 1;
    const currentResult = bookResults[currentBookIndex];
    const { passingLevel } = currentResult;
    if (
        passingLevel === TRO_PASSING_LEVEL_STATUSES.PASS ||
        passingLevel === TRO_PASSING_LEVEL_STATUSES.NO_PASS
    ) {
        return true;
    }
    return false;
});

// Check if student has completed all available forms for Maze
// mazeReadyForNextScript values are being checked because
// StartView component (where this selector is being used) shares between 2 screens: login and done assessment
// Check that stadent hasn't completed the assessment and there are no forms left
selectors.getHasCompletedAllPmForms = createSelector(
    [
        selectors.getActiveObsMeasures,
        selectors.getMazeReadyForNextScript,
        selectors.getHasAvailableForms,
    ],
    (activeObsMeasures, mazeReadyForNextScript, hasAvailableForms) => {
        if (activeObsMeasures.includes(COMPREHENSION_MEASURE.MAZE)) {
            return !mazeReadyForNextScript && !hasAvailableForms;
        }
        return false;
    },
);

selectors.getIsAssessmentCompleted = createSelector(
    [selectors.getAssessmentType, selectors.getIsPlaylistCompleted, selectors.getHasAvailableForms],
    (assessmentType, isPlaylistCompleted, hasAvailableForms) =>
        isPlaylistCompleted && (assessmentType === ASSESSMENT_TYPE.BM ? true : !hasAvailableForms),
);

export default selectors;
